import { ETextStyleVariant, Text } from '@outdoorsyco/bonfire';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import AddressInput from '@/components/switchback/AddressInput/AddressInput';
import { IAutocompleteOption } from '@/components/switchback/Autocomplete/AutocompleteOptions';
import { ESearchFilters } from '@/constants/searchFilters';
import { EFilterMethod, setFormFilter } from '@/redux/modules/searchForm';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getSearchResultsMeta } from '@/redux/selectors/search/meta';
import { getSearchFormDeliveryFilter } from '@/redux/selectors/search/searchForm';
import {
  EDeliveryFields,
  EDeliveryOption,
  TDeliveryFields,
} from '@/services/types/core/delivery.types';

interface IBringItToMeFilterProps {
  onUserInteraction?: () => void;
}

const BringItToMeFilter: React.FC<IBringItToMeFilterProps> = ({ onUserInteraction }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const form = useForm<TDeliveryFields>();

  const {
    [ESearchFilters.DELIVERY_ADDRESS]: deliveryAddressFromStore,
    [ESearchFilters.DELIVERY]: isDeliveryFromStore,
  } = useSelector(getSearchFormDeliveryFilter);
  const { [ESearchFilters.DELIVERY_ADDRESS]: deliveryAddressFromQuery } =
    useSelector(getQueryParams);

  // Get search location for AddressInput placesOption
  const searchMeta = useSelector(getSearchResultsMeta);
  const location = searchMeta ? { lng: searchMeta.lng, lat: searchMeta.lat } : undefined;

  const [addressValue, setAddressValue] = useState<string>('');

  // Track previous delivery state to detect when filter is reset
  const prevDeliveryState = useRef<{ isDelivery: boolean; hasAddress: boolean }>({
    isDelivery: !!isDeliveryFromStore,
    hasAddress: !!deliveryAddressFromStore,
  });

  // Set initial value from query params or store
  useEffect(() => {
    // Handle the case where deliveryAddressFromQuery can be string or string[]
    const addressFromQueryParam = Array.isArray(deliveryAddressFromQuery)
      ? deliveryAddressFromQuery[0]
      : deliveryAddressFromQuery;

    const initialAddress = deliveryAddressFromStore || addressFromQueryParam || '';

    if (initialAddress && initialAddress !== addressValue) {
      setAddressValue(initialAddress);
      form.setValue(EDeliveryFields.DELIVERY_ADDRESS, initialAddress);
    }
  }, [deliveryAddressFromStore, deliveryAddressFromQuery, form, addressValue]);

  // Reset local state when delivery filter is cleared from store
  useEffect(() => {
    const currentDeliveryState = {
      isDelivery: !!isDeliveryFromStore,
      hasAddress: !!deliveryAddressFromStore,
    };

    // Check if we went from having delivery/address to not having them (filter was reset)
    const wasReset =
      (prevDeliveryState.current.isDelivery || prevDeliveryState.current.hasAddress) &&
      !currentDeliveryState.isDelivery &&
      !currentDeliveryState.hasAddress;

    if (wasReset) {
      setAddressValue('');
      form.setValue(EDeliveryFields.DELIVERY_ADDRESS, '');
    }

    // Update the ref for next comparison
    prevDeliveryState.current = currentDeliveryState;
  }, [isDeliveryFromStore, deliveryAddressFromStore, form]);

  const handleAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;
      setAddressValue(value);
      form.setValue(EDeliveryFields.DELIVERY_ADDRESS, value);

      // Notify parent of user interaction
      onUserInteraction?.();

      if (value.length === 0) {
        // Clear the delivery filter when input is empty
        dispatch(
          setFormFilter({
            filters: {
              [ESearchFilters.DELIVERY]: false,
              [ESearchFilters.DELIVERY_ADDRESS]: '',
              [ESearchFilters.ADDRESS]: '',
              [ESearchFilters.DELIVERY_STATIONARY]: EDeliveryOption.MOVING,
            },
            filterMethod: EFilterMethod.INPUT,
          }),
        );
      } else {
        // Set both address and deliveryAddress for moving delivery, similar to SearchHeader
        dispatch(
          setFormFilter({
            filters: {
              [ESearchFilters.DELIVERY]: true,
              [ESearchFilters.DELIVERY_ADDRESS]: value,
              [ESearchFilters.ADDRESS]: value,
              [ESearchFilters.DELIVERY_STATIONARY]: EDeliveryOption.MOVING,
            },
            filterMethod: EFilterMethod.INPUT,
          }),
        );
      }
    },
    [dispatch, form, onUserInteraction],
  );

  const handleSelectAddress = useCallback(
    (option: IAutocompleteOption) => {
      const selectedAddress = option.label;
      setAddressValue(selectedAddress);
      form.setValue(EDeliveryFields.DELIVERY_ADDRESS, selectedAddress);

      // Set both address and deliveryAddress for moving delivery, similar to SearchHeader
      dispatch(
        setFormFilter({
          filters: {
            [ESearchFilters.DELIVERY]: true,
            [ESearchFilters.DELIVERY_ADDRESS]: selectedAddress,
            [ESearchFilters.ADDRESS]: selectedAddress,
            [ESearchFilters.DELIVERY_STATIONARY]: EDeliveryOption.MOVING,
          },
          filterMethod: EFilterMethod.INPUT,
        }),
      );
    },
    [dispatch, form],
  );

  return (
    <>
      <Text variant={ETextStyleVariant.MediumRegular} className="mb-6 text-gray-50">
        {intl.formatMessage({
          defaultMessage: 'Enter a location or address to browse RVs that can be brought to you.',
          id: 'PplRo0',
          description: 'Bring it to me filter modal description',
        })}
      </Text>

      <div className="mb-6">
        <AddressInput
          form={form}
          required={false}
          addressValue={addressValue}
          name={EDeliveryFields.DELIVERY_ADDRESS}
          onChangeAddress={handleAddressChange}
          onSelectAddress={handleSelectAddress}
          placesOption={{
            location: [location?.lng || 0, location?.lat || 0],
            types: 'address,region,place,district,locality,poi',
          }}
          popupOptions={{
            disablePortal: false,
            anchorReference: 'anchorEl',
            disableScrollLock: true,
          }}
          label={intl.formatMessage({
            defaultMessage: 'Enter location or address',
            id: 'rG8Jtc',
          })}
          popupClassName="p-8 shadow-100 rounded-box !z-[1450] mt-1"
          skipFocusListener
          onFocus={e => e.target.select()}
        />
      </div>
    </>
  );
};

export default BringItToMeFilter;
